import moment from 'moment'
import { useEffect } from 'react'
import {
    useListTicketPersonsQuery,
    useListTicketPriceByTypesQuery,
    useListTicketZonesByPersonQuery,
} from 'store/api-slice/hooks'
import { Person, Time, Zone } from 'store/api-slice/types'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { setTicketTranslations, Translation } from 'store/selfcare-slice'
import { useApp } from 'store/steps'

const useTicketTranslations = () => {
    const dispatch = useAppDispatch()
    const { language } = useApp()

    // TODO: it needs to be removed from selfcare store
    // and move into global store or just context
    const { persons, zones, tickets } = useAppSelector(
        (state) => state.selfcare.ticketTranslations
    )

    const areTranslationsReady = !!persons && !!zones && !!tickets

    const {
        data: personTypes,
        isFetching: arePersonsFetching,
        isError: isPersonsError,
        refetch: refetchPersons,
    } = useListTicketPersonsQuery({
        _locale: language,
    })

    const {
        data: zoneTypes,
        isFetching: areZonesFetching,
        isError: isZonesError,
        refetch: refetchZones,
    } = useListTicketZonesByPersonQuery(
        {
            _locale: language,
            person: 'adult',
        },
        { refetchOnMountOrArgChange: true }
    )

    const tomorrow = moment().add(1, 'days').format('YYYY-MM-DD').toString()
    const {
        data: ticketTypes,
        isFetching: areTicketsFetching,
        isError: isTicketsError,
        refetch: refetchTypes,
    } = useListTicketPriceByTypesQuery({
        _locale: language,
        person: 'adult',
        startDate: tomorrow,
        zone: 'saunaria',
    })

    const isTranslationsError = isPersonsError || isZonesError || isTicketsError

    const refetch = () => {
        refetchPersons()
        refetchZones()
        refetchTypes()
    }

    useEffect(() => {
        if (arePersonsFetching || !personTypes?.persons) return

        const translatedPersons = personTypes.persons.map(
            ({ key, title, description }) => ({
                key,
                title: `${title}${description ? ` (${description})` : ''}`,
            })
        )

        dispatch(
            setTicketTranslations({
                type: 'persons',
                data: [...translatedPersons],
            })
        )
    }, [arePersonsFetching, personTypes])

    useEffect(() => {
        if (areZonesFetching || !zoneTypes?.zones) return

        dispatch(
            setTicketTranslations({
                type: 'zones',
                data: zoneTypes.zones.map(({ key, title }) => ({
                    key,
                    title,
                })),
            })
        )
    }, [areZonesFetching, zoneTypes])

    useEffect(() => {
        if (areTicketsFetching || !ticketTypes?.tickets) return

        const ticketsTypesForSelfcare: Translation[] = ticketTypes.tickets.map(
            ({ time, title }) => {
                // shorten name by cutting the name from between quotes
                const titleMatched = title?.match(/(?<=[„"])(.*)(?=["”])/g)
                return {
                    key: time,
                    title: titleMatched?.length ? titleMatched[0] : title,
                }
            }
        )

        dispatch(
            setTicketTranslations({
                type: 'tickets',
                data: [...ticketsTypesForSelfcare],
            })
        )
    }, [areTicketsFetching, ticketTypes])

    const getTicketPersonByKey = (personKey: Person) => {
        const translated = persons?.find(
            (person) => person.key === personKey
        )?.title

        return translated || null
    }

    const getTicketZoneByKey = (zoneKey: Zone) => {
        const translated = zones?.find((zone) => zone.key === zoneKey)?.title

        return translated || null
    }

    const getTicketTypeByKey = (timeKey: Time) => {
        const translated = tickets?.find(
            (ticket) => ticket.key === timeKey
        )?.title

        return translated || null
    }

    return {
        getTicketPersonByKey,
        getTicketZoneByKey,
        getTicketTypeByKey,
        areTranslationsReady,
        isTranslationsError,
        refetch,
    }
}

export default useTicketTranslations
